import { useCallback, useEffect, useState, useMemo } from 'react'
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  type NodeTypes,
  type EdgeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { cn } from '@trafiflow/shared-ui'
import type { AdGenStageStatus } from '@/services/ad-generator/types'
import PipelineNode from './PipelineNode'
import PipelineEdge from './PipelineEdge'
import PipelineControls from './PipelineControls'
import StageDetailPanel from './StageDetailPanel'
import { usePipelineNodes } from './hooks/usePipelineNodes'
import { usePipelineLayout, type LayoutMode } from './hooks/usePipelineLayout'

// Register custom node and edge types
const nodeTypes: NodeTypes = {
  pipelineNode: PipelineNode as unknown as NodeTypes[string],
}

const edgeTypes: EdgeTypes = {
  pipelineEdge: PipelineEdge,
}

interface PipelineGraphProps {
  stages: AdGenStageStatus[]
  currentStage: string
  jobId: string
  jobStatus: string
  className?: string
}

function PipelineGraphInner({
  stages,
  currentStage,
  jobId,
  className,
}: Omit<PipelineGraphProps, 'jobStatus'>) {
  const [selectedStage, setSelectedStage] = useState<AdGenStageStatus | null>(null)
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('dagre')
  const { fitView } = useReactFlow()
  const { applyLayout } = usePipelineLayout()

  // Handle stage click
  const handleStageClick = useCallback((stageName: string) => {
    const stage = stages.find((s) => s.name === stageName)
    if (stage) {
      setSelectedStage(stage)
    }
  }, [stages])

  // Generate nodes and edges from stages
  const { nodes: rawNodes, edges: rawEdges } = usePipelineNodes(
    stages,
    currentStage,
    handleStageClick
  )

  // Apply layout
  const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(() => {
    return applyLayout(rawNodes, rawEdges, layoutMode)
  }, [rawNodes, rawEdges, layoutMode, applyLayout])

  // State for ReactFlow
  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes as never[])
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges)

  // Update nodes/edges when layout changes
  useEffect(() => {
    setNodes(layoutedNodes as never[])
    setEdges(layoutedEdges)
  }, [layoutedNodes, layoutedEdges, setNodes, setEdges])

  // Fit view on initial load and when nodes change
  useEffect(() => {
    const timer = setTimeout(() => {
      fitView({ padding: 0.2, duration: 200 })
    }, 100)
    return () => clearTimeout(timer)
  }, [nodes.length, fitView])

  // Handle fit view button
  const handleFitView = useCallback(() => {
    fitView({ padding: 0.2, duration: 300 })
  }, [fitView])

  // Toggle layout mode
  const handleToggleLayout = useCallback(() => {
    setLayoutMode((prev) => (prev === 'dagre' ? 'grid' : 'dagre'))
  }, [])

  // Close detail panel
  const handleCloseDetail = useCallback(() => {
    setSelectedStage(null)
  }, [])

  return (
    <div className={cn('relative w-full h-full', className)}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.5}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        proOptions={{ hideAttribution: true }}
        className="bg-tf-bg"
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="rgba(255, 255, 255, 0.05)"
        />
        <PipelineControls
          onFitView={handleFitView}
          onToggleLayout={handleToggleLayout}
        />
      </ReactFlow>

      {/* Stage detail panel */}
      {selectedStage && (
        <StageDetailPanel
          stage={selectedStage}
          jobId={jobId}
          onClose={handleCloseDetail}
        />
      )}
    </div>
  )
}

// Wrap with ReactFlowProvider
export default function PipelineGraph({ jobStatus: _, ...props }: PipelineGraphProps) {
  return (
    <ReactFlowProvider>
      <PipelineGraphInner {...props} />
    </ReactFlowProvider>
  )
}
